import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle2, XCircle, ImageIcon, GitCompare } from 'lucide-react';
import { api } from '../../lib/api';

const STAGE_LABELS = {
  image_availability: 'Descarga / validación de imagen',
  photo_upload: 'Subida de foto (Facebook)',
  carousel_feed: 'Publicación del carrusel (Facebook feed)',
  item_create: 'Creación de ítem del carrusel (Instagram)',
  item_poll: 'Procesamiento del ítem — esperar FINISHED (Instagram)',
  item_create_or_poll: 'Creación / procesamiento de ítems (Instagram)',
  carousel_create: 'Creación del contenedor carrusel (Instagram)',
  carousel_poll: 'Procesamiento del carrusel (Instagram)',
  media_publish: 'Publicación final (Instagram)',
  single_photo: 'Imagen única (Facebook)',
  single_media_create: 'Imagen única (Instagram)',
};

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex gap-2 text-xs py-0.5">
      <span className="text-gray-500 shrink-0 w-40">{label}</span>
      <span className="text-habita-dark font-mono break-all">{String(value)}</span>
    </div>
  );
}

function MetaErrorBlock({ err }) {
  if (!err) return null;
  return (
    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
      <p className="text-xs font-bold text-red-700 mb-1">Error exacto de Meta</p>
      <Row label="message" value={err.message} />
      <Row label="type" value={err.type} />
      <Row label="code" value={err.code} />
      <Row label="error_subcode" value={err.error_subcode} />
      <Row label="fbtrace_id" value={err.fbtrace_id} />
      <Row label="error_user_title" value={err.error_user_title} />
      <Row label="error_user_msg" value={err.error_user_msg} />
    </div>
  );
}

function Probe({ p }) {
  if (!p) return null;
  const ok = p.ok;
  return (
    <div className="border border-gray-100 rounded-lg p-2 bg-white">
      <div className="flex items-center gap-1.5 mb-1">
        {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-red-600" />}
        <span className="text-[11px] font-bold text-habita-dark">{p.variant || 'imagen'}</span>
      </div>
      <Row label="HTTP status" value={p.http_status} />
      <Row label="Content-Type" value={p.content_type} />
      <Row label="Tamaño (bytes)" value={p.size} />
      <Row label="Intentos" value={p.attempts} />
      <Row label="URL" value={p.url} />
      {p.error && <Row label="Error" value={p.error} />}
      {p.flags && p.flags.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {p.flags.map((f, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function DiagEntry({ d }) {
  const stage = STAGE_LABELS[d.stage] || d.stage;
  return (
    <div className="border border-gray-200 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">
          {d.channel} · {stage}
        </span>
        {typeof d.index === 'number' && (
          <span className="text-[11px] text-gray-500">imagen #{d.index}</span>
        )}
        {d.used && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">usó: {d.used}</span>}
        {d.discarded && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">descartada</span>}
      </div>
      {d.status_code && <Row label="status_code (IG)" value={d.status_code} />}
      {d.status_detail && <Row label="status detalle" value={d.status_detail} />}
      {d.url && <Row label="URL enviada" value={d.url} />}
      {(d.probes || []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {d.probes.map((p, i) => <Probe key={i} p={p} />)}
        </div>
      )}
      <MetaErrorBlock err={d.meta_error} />
    </div>
  );
}

function PropDiagnosis({ title, data }) {
  if (!data) return null;
  const okAll = data.usable_count === data.image_count && data.image_count > 0;
  return (
    <div className="border border-gray-200 rounded-xl p-3">
      <p className="font-bold text-sm text-habita-dark truncate">{title}</p>
      <p className="text-xs text-gray-600 truncate">{data.property_title}</p>
      <div className={`mt-1.5 text-xs font-semibold flex items-center gap-1.5 ${okAll ? 'text-green-700' : 'text-red-700'}`}>
        {okAll ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {data.verdict}
      </div>
      <p className="text-[11px] text-gray-500 mt-1">
        {data.usable_count}/{data.image_count} imágenes publicables por Meta
      </p>
      {data.flags_summary && data.flags_summary.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {data.flags_summary.map((f, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{f}</span>
          ))}
        </div>
      )}
      <div className="mt-2 space-y-1">
        {(data.images || []).map((im) => (
          <div key={im.index} className="flex items-center gap-2 text-[11px]">
            {im.usable ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
            <span className="text-gray-500">#{im.index}</span>
            <span className="font-mono text-gray-700">HTTP {im.raw?.http_status ?? '—'}</span>
            <span className="text-gray-500 truncate">{im.raw?.content_type || ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DistributionDiagnosticModal({ log, onClose }) {
  const [full, setFull] = useState(log);
  const [diag, setDiag] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const f = await api.distributionLog(log.id);
        if (alive) setFull(f);
      } catch { /* usa el log de la lista */ }
    })();
    if (log.property_id) {
      setLoadingDiag(true);
      (async () => {
        try {
          const d = await api.diagnoseDistribution(log.property_id);
          if (alive) setDiag(d);
        } catch { /* noop */ }
        finally { if (alive) setLoadingDiag(false); }
      })();
    }
    return () => { alive = false; };
  }, [log.id, log.property_id]);

  const diagnostics = full?.diagnostics || [];
  const stage = full?.failed_stage;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="dist-diag-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-habita-dark">Diagnóstico de publicación</h3>
            <p className="text-xs text-gray-500">{full?.channel_label} · {full?.property_title || full?.property_id}</p>
          </div>
          <button data-testid="dist-diag-close" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Resumen del fallo */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <span>{full?.created_at ? new Date(full.created_at).toLocaleString('es-CO') : ''}</span>
              {full?.trigger && <span>· origen: {full.trigger}</span>}
            </div>
            <p className="text-sm font-semibold text-red-700">{full?.error || 'Sin mensaje de error'}</p>
            {stage && (
              <p className="text-xs text-red-600 mt-1">
                Etapa donde falló: <b>{STAGE_LABELS[stage] || stage}</b>
              </p>
            )}
          </div>

          {/* Diagnóstico por imagen (lo que Meta respondió) */}
          <div>
            <p className="text-sm font-bold text-habita-dark mb-2 flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4" /> Detalle por imagen y respuesta de Meta
            </p>
            {diagnostics.length === 0 ? (
              <p className="text-xs text-gray-500">Este registro no tiene diagnóstico por imagen (creado antes de la instrumentación o publicación exitosa).</p>
            ) : (
              <div className="space-y-2" data-testid="dist-diag-entries">
                {diagnostics.map((d, i) => <DiagEntry key={i} d={d} />)}
              </div>
            )}
          </div>

          {/* Comparación de URLs como las ve Meta */}
          <div>
            <p className="text-sm font-bold text-habita-dark mb-2 flex items-center gap-1.5">
              <GitCompare className="w-4 h-4" /> Verificación de imágenes &ldquo;como las ve Meta&rdquo;
            </p>
            {loadingDiag ? (
              <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Verificando URLs públicas…</div>
            ) : diag ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dist-diag-compare">
                <PropDiagnosis title="Este inmueble (falló)" data={diag.target} />
                {diag.comparison ? (
                  <PropDiagnosis title="Comparado con (publicó bien)" data={diag.comparison} />
                ) : (
                  <div className="border border-dashed border-gray-200 rounded-xl p-3 text-xs text-gray-500 flex items-center">
                    {diag.comparison_source?.reason
                      ? `Sin comparación automática (${diag.comparison_source.reason}).`
                      : 'Aún no hay una publicación exitosa de Meta para comparar.'}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No se pudo ejecutar la verificación.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
